import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loading, Empty, Tag } from 'react-vant';
import { ArrowLeft, AwardO, CalendarO, MedalO } from '@react-vant/icons';
import request from '../../api/request';

const AthleteProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [results, setResults] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [studentRes, schedulesRes, resultsRes] = await Promise.all([
          request.get(`/v2/academic/students/${id}/`),
          request.get('/v2/sports/schedules/', { params: { student_id: id, page_size: 50 } }),
          request.get('/v2/sports/results/', { params: { student_id: id, page_size: 50 } })
        ]);
        setStudent(studentRes);
        // 过滤掉已过期的赛程，只显示今天及以后的
        const today = new Date().toISOString().split('T')[0];
        const upcoming = (schedulesRes.items || []).filter(s => s.scheduled_date >= today);
        setSchedules(upcoming);
        setResults(resultsRes.items || []);
      } catch (error) {
        console.error('获取运动员信息失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--canvas-bg, #f8fafc)' }}>
        <Loading type="spinner" color="#056b4f" vertical>加载运动员档案...</Loading>
      </div>
    );
  }

  if (!student) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--canvas-bg, #f8fafc)', padding: '60px 16px' }}>
        <Empty description="未找到该运动员档案" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas-bg, #f8fafc)', paddingBottom: 60 }}>
      {/* 沉浸式星瀚空天蓝页眉 */}
      <div style={{
        background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
        padding: '44px 16px 40px',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰半透圆 */}
        <div style={{
          position: 'absolute', top: -30, right: -30,
          width: 140, height: 140, borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)'
        }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <div
            onClick={() => navigate(-1)}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft color="#fff" fontSize={18} />
          </div>

          <div style={{ textAlign: 'center' }}>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px' }}>
              运动员档案
            </h1>
          </div>

          <div style={{ width: 36 }} />
        </div>
      </div>

      {/* 头部学生档案主卡片 */}
      <div style={{ padding: '0 16px', marginTop: '-24px', position: 'relative', zIndex: 10 }}>
        <div style={{
          background: '#ffffff',
          borderRadius: 18,
          padding: '20px 18px',
          boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{ 
            width: 64, height: 64, borderRadius: 32,
            background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            marginRight: 16, color: '#ffffff', fontSize: 26, fontWeight: 800,
            boxShadow: '0 6px 16px rgba(5, 107, 79, 0.28)',
            flexShrink: 0
          }}>
            {student.real_name?.[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
              {student.real_name}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {student.class_name && (
                <span style={{
                  background: '#f0fdf4', color: '#056b4f', fontSize: 12,
                  fontWeight: 600, padding: '2px 8px', borderRadius: 6
                }}>
                  {student.class_name}
                </span>
              )}
              {student.student_no && (
                <span style={{
                  background: '#f8fafc', color: '#64748b', fontSize: 12,
                  fontWeight: 500, padding: '2px 8px', borderRadius: 6,
                  border: '1px solid #e2e8f0'
                }}>
                  学号: {student.student_no}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 即将参加的项目 */}
      {schedules.length > 0 && (
        <div style={{ padding: '0 16px', marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <div style={{ width: 4, height: 16, background: '#056b4f', borderRadius: 2 }} />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>即将参加的项目</h3>
            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 'auto' }}>
              共 {schedules.length} 项
            </span>
          </div>

          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: '4px 16px',
            boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
            border: '1px solid #f1f5f9'
          }}>
            {schedules.slice(0, 5).map((item, idx) => {
              const isLast = idx === Math.min(schedules.length, 5) - 1;
              return (
                <div key={idx} style={{ 
                  display: 'flex',
                  alignItems: 'center', 
                  padding: '14px 0',
                  borderBottom: isLast ? 'none' : '1px solid #f1f5f9'
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: '#f0fdf4',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginRight: 12, color: '#056b4f', flexShrink: 0
                  }}>
                    <CalendarO fontSize={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                      {item.event_name}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                      📅 {item.scheduled_date} · 📍 {item.venue || '跑道'}
                    </div>
                  </div>
                  <Tag
                    round
                    style={{
                      background: '#f0fdf4',
                      color: '#056b4f',
                      border: 'none',
                      fontWeight: 600,
                      padding: '2px 8px'
                    }}
                  >
                    待参赛
                  </Tag>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 历史成绩 */}
      <div style={{ padding: '0 16px', marginTop: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <div style={{ width: 4, height: 16, background: '#ff5e1e', borderRadius: 2 }} />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>赛事历史战绩</h3>
        </div>

        {results.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {results.map((res, idx) => (
              <div key={idx} style={{ 
                background: '#ffffff',
                borderRadius: 16,
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                border: '1px solid #f1f5f9'
              }}>
                <div style={{ 
                  width: 44, height: 44, borderRadius: 12,
                  background: res.rank === 1 ? '#fffbeb' : (res.rank === 2 ? '#f8fafc' : (res.rank === 3 ? '#fff7ed' : '#f0fdf4')), 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', 
                  marginRight: 14, flexShrink: 0
                }}>
                  <AwardO style={{
                    color: res.rank === 1 ? '#fa8c16' : (res.rank === 2 ? '#64748b' : (res.rank === 3 ? '#ea580c' : '#056b4f')),
                    fontSize: 22
                  }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                    {res.event_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {res.created_at?.split('T')[0] || '运动会'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#056b4f' }}>
                    {res.performance || res.performance_value}
                  </div>
                  <div style={{
                    fontSize: 11,
                    color: res.rank <= 3 ? '#ff5e1e' : '#64748b',
                    fontWeight: 700,
                    marginTop: 2
                  }}>
                    第 {res.rank} 名
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: '36px 16px',
            textAlign: 'center',
            color: '#94a3b8',
            border: '1px solid #f1f5f9'
          }}>
            暂无比赛成绩记录
          </div>
        )}
      </div>
    </div>
  );
};

export default AthleteProfile;
